import React from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { isEmpty, filter } from 'lodash'
import Fuse from 'fuse.js'
import { useHistory } from 'react-router-dom'

import Layout from 'containers/Layout'
import ItemIndex from 'containers/ItemsIndex'
import useDataRefresh from 'hooks/useDataRefresh'
import paginationCoordinator from 'utils/paginationCoordinator'

import { fetchConfigurations } from 'store/Configurations/actions'
import { fetchDevices } from 'store/Devices/actions'
import { setConfiguredPOSVisible } from 'store/AppState/actions'
import {
  getConfigurationsWithDevicesAndPrinters,
  getConfigurationSearchQuery,
} from 'store/Configurations/selector'
import { getDevicesById } from 'store/Devices/selector'
import { getCurrentVenueId } from 'store/Venues/selector'
import { getIsConfiguredPOSVisible } from 'store/AppState/selector'
import { fetchPrinters } from 'store/Printers/actions'

const FUSE_OPTIONS = {
  threshold: 0.1,
  location: 0,
  distance: 100,
  maxPatternLength: 32,
  minMatchCharLength: 1,
  keys: ['name', 'uuid', 'deviceName', 'deviceId', 'printerName', 'printerId'],
}
const FILTER_CONSTRAINTS = 'deviceId'

export const ConfigurationsIndexPage = (props) => {
  const history = useHistory()

  const {
    fetchConfigurations,
    fetchDevices,
    fetchPrinters,
    setConfiguredPOSVisible,
    configurations,
    currentVenueId,
    searchQuery,
    isConfiguredPOSVisible,
  } = props

  const fetchData = () => {
    if (isEmpty(currentVenueId)) {
      return Promise.resolve()
    } else {
      return Promise.all([
        fetchConfigurations(currentVenueId),
        fetchDevices(currentVenueId),
        fetchPrinters(currentVenueId),
      ])
    }
  }

  const [isLoadingDevices] = useDataRefresh(fetchData, [currentVenueId])

  const preFilteredConfigurations = isConfiguredPOSVisible
    ? filter(configurations, FILTER_CONSTRAINTS)
    : configurations

  const renderedConfigurations = isEmpty(searchQuery)
    ? preFilteredConfigurations
    : new Fuse(preFilteredConfigurations, FUSE_OPTIONS).search(searchQuery)

  const paginator = paginationCoordinator(renderedConfigurations, history)

  return (
    <Layout
      checkboxLabel="Show configured"
      handleSwitch={setConfiguredPOSVisible}
      isSidebar={true}
      isSwitchChecked={isConfiguredPOSVisible}
    >
      <ItemIndex
        devices={paginator}
        isFilteringItems={!isEmpty(searchQuery)}
        isLoadingDevices={isLoadingDevices}
      />
    </Layout>
  )
}

ConfigurationsIndexPage.propTypes = {
  fetchConfigurations: PropTypes.func.isRequired,
  fetchDevices: PropTypes.func.isRequired,
  fetchPrinters: PropTypes.func.isRequired,
  configurations: PropTypes.array.isRequired,
  // eslint-disable-next-line react/no-unused-prop-types
  devices: PropTypes.object.isRequired,
  currentVenueId: PropTypes.string,
  searchQuery: PropTypes.string.isRequired,
  setConfiguredPOSVisible: PropTypes.func,
  isConfiguredPOSVisible: PropTypes.bool.isRequired,
}

const mapStateToProps = (state) => ({
  isConfiguredPOSVisible: getIsConfiguredPOSVisible(state),
  configurations: getConfigurationsWithDevicesAndPrinters(state),
  devices: getDevicesById(state),
  currentVenueId: getCurrentVenueId(state),
  searchQuery: getConfigurationSearchQuery(state),
})

const mapDispatchToProps = {
  fetchConfigurations,
  fetchDevices,
  fetchPrinters,
  setConfiguredPOSVisible,
}

export default connect(mapStateToProps, mapDispatchToProps)(ConfigurationsIndexPage)
